import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, SetStateAction, useState } from 'react';
import { PasscodeList } from '@/types/registration.types';
import PasscodeListCardRow from '../../passcode-lists/passcode-list-row';
import SearchInput from '@ui/search-input/search-input';
import { AddNewPasscodeListMember } from '@/connection/audiences/passcode-lists';
import { useAppSelector } from '@/store/hooks';
import { selectProjectChannel } from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { NavLink } from 'react-router-dom';
import OptionalComponent from '@ui/optional-component/optional-component';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';

type RegistrantInfo = {
  email: string;
  first_name?: string;
  last_name?: string;
};

type AddRegistrantToListProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  passcodeLists: PasscodeList[];
  registrants?: RegistrantInfo[];
  onSaveComplete?: () => void;
};

const AddRegistrantToList = ({
  open,
  setOpen,
  passcodeLists,
  registrants = [],
  onSaveComplete,
}: AddRegistrantToListProps) => {
  const channel = useAppSelector(selectProjectChannel);

  const [searchValue, setSearchValue] = useState('');
  const [selectedPasscodeLists, setSelectedPasscodeLists] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const filteredLists = passcodeLists.filter((list) => {
    if (!searchValue) return true;
    const regex = new RegExp(`\\b${searchValue}`, 'i');
    return regex.test(list.name);
  });

  const handleClose = () => {
    setSelectedPasscodeLists([]);
    setOpen(false);
  };

  const handleSearch = (search: string) => {
    setSearchValue(search.toLowerCase());
  };

  const toggleSelectToAdd = (passcodeListId: number) => {
    setSelectedPasscodeLists((prevLists) => {
      if (prevLists.includes(passcodeListId)) {
        return prevLists.filter((id) => id !== passcodeListId);
      }
      return [...prevLists, passcodeListId];
    });
  };

  const handleSave = async () => {
    if (!channel || selectedPasscodeLists.length === 0 || registrants.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      // Add registrants to each selected list
      const addPromises: Promise<unknown>[] = [];

      for (const listId of selectedPasscodeLists) {
        for (const registrant of registrants) {
          addPromises.push(
            AddNewPasscodeListMember(
              listId,
              '', // passcode - empty string for auto-generation if needed
              registrant.email,
              registrant.first_name || '',
              registrant.last_name || ''
            )
          );
        }
      }

      await Promise.all(addPromises);

      toastTrigger('Successfully added registrants to lists', {
        type: EToastTypes.success,
        autoClose: 5000,
      });

      if (onSaveComplete) {
        onSaveComplete();
      }

      handleClose();
    } catch (error) {
      console.error('Error adding registrants to lists:', error);
      toastTrigger('Error adding registrants to lists', {
        description: 'Please try again',
        type: EToastTypes.error,
        autoClose: 10000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      customSize={{
        width: '600px',
        height: 'auto',
        minHeight: '300px',
        maxHeight: '560px',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-24 overflow-y-auto flex-1 flex-column"
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={() => (
        <Container className="flex-column gap-8" borderRadius="16px">
          <Text className="H4">Add to list</Text>
          <Text className="P1" color="var(--gray-500)">
            Add audience members to lists
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
          <Button
            onClick={handleClose}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
            className="L1 padding-v-16 padding-h-24"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="padding-v-16 padding-h-24"
            disabled={isSaving || selectedPasscodeLists.length === 0}
            loading={isSaving}
            disabledOpacity={0.6}
            backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
          >
            <Text
              className="L1"
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            >
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="flex-column padding-v-24 gap-8">
        <OptionalComponent display={filteredLists.length > 0}>
          <>
            <Container className="padding-b-8">
              <SearchInput onSubmit={handleSearch} debounceTime={300} onClear={() => handleSearch('')} />
            </Container>
            {filteredLists.map((passcodeList: PasscodeList) => {
              return (
                <PasscodeListCardRow
                  key={passcodeList.passcode_list}
                  passcodeList={passcodeList}
                  onClick={toggleSelectToAdd as Dispatch<SetStateAction<number>>}
                />
              );
            })}
          </>
        </OptionalComponent>
        <OptionalComponent display={filteredLists.length === 0}>
          <Container className="flex-column gap-16 ai-center jc-center padding-v-32">
            <Text className="P1" color="var(--gray-500)">
              No lists available. Create a list in your channel to get started.
            </Text>
            <NavLink to="/admin/people" onClick={handleClose}>
              <Button
                className="padding-v-16 padding-h-24"
                disabledOpacity={0.6}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Manage lists
                </Text>
              </Button>
            </NavLink>
          </Container>
        </OptionalComponent>
      </Container>
    </Modal>
  );
};

export default AddRegistrantToList;
